import type { SupplierCertificateType } from '@tandemone/types'

export type CertFamily =
  | 'quality'
  | 'process'
  | 'environmental'
  | 'safety'
  | 'compliance'

export type CertFamilyInfo = {
  family: CertFamily
  label: string
  // tonal tailwind utilities for the badge background and text
  className: string
}

const FAMILY_LABELS: Record<CertFamily, string> = {
  quality: 'Quality management',
  process: 'Process & welding',
  environmental: 'Environmental',
  safety: 'Safety',
  compliance: 'Compliance & trade'
}

const FAMILY_CLASSES: Record<CertFamily, string> = {
  quality:
    'bg-blue-100 text-blue-900 border-transparent dark:bg-blue-950 dark:text-blue-200',
  process:
    'bg-purple-100 text-purple-900 border-transparent dark:bg-purple-950 dark:text-purple-200',
  environmental:
    'bg-emerald-100 text-emerald-900 border-transparent dark:bg-emerald-950 dark:text-emerald-200',
  safety:
    'bg-amber-100 text-amber-900 border-transparent dark:bg-amber-950 dark:text-amber-200',
  compliance:
    'bg-slate-100 text-slate-900 border-transparent dark:bg-slate-800 dark:text-slate-200'
}

const FAMILY_BY_CERT: Record<SupplierCertificateType, CertFamily> = {
  iso_9001: 'quality',
  as9100: 'quality',
  iatf_16949: 'quality',
  ts_16949: 'quality',
  iso_13485: 'quality',
  iso_3834: 'process',
  nadcap: 'process',
  lean_manufacturing_certificate: 'process',
  six_sigma_certificate: 'process',
  iso_14001: 'environmental',
  reach_compliant: 'environmental',
  rohs_compliant: 'environmental',
  iso_45001: 'safety',
  itar_registered: 'compliance',
  ce_marking: 'compliance',
  ul_certified: 'compliance',
  ul_listed: 'compliance',
  iec_61730: 'compliance'
}

export function getCertFamily(cert: SupplierCertificateType): CertFamilyInfo {
  const family = FAMILY_BY_CERT[cert]
  return {
    family,
    label: FAMILY_LABELS[family],
    className: FAMILY_CLASSES[family]
  }
}

export const CERT_FAMILY_ORDER: ReadonlyArray<CertFamily> = [
  'quality',
  'process',
  'environmental',
  'safety',
  'compliance'
]

export const CERT_FAMILY_LABELS = FAMILY_LABELS

const FAMILY_RANK: Record<string, number> = Object.fromEntries(
  CERT_FAMILY_ORDER.map((family, index) => [family, index])
)

/** group by family so colors cluster left-to-right, oldest first within one. */
export function sortByCertFamily<
  TCert extends { certification: SupplierCertificateType; createdAt: string }
>(certificates: TCert[]): TCert[] {
  return certificates.slice().sort((left, right) => {
    const leftRank = FAMILY_RANK[getCertFamily(left.certification).family] ?? 99
    const rightRank =
      FAMILY_RANK[getCertFamily(right.certification).family] ?? 99
    if (leftRank !== rightRank) return leftRank - rightRank
    return left.createdAt.localeCompare(right.createdAt)
  })
}
